import { useEffect, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * The "רשימות ישובים" (distribution lists) maintained in the admin panel, under
 * הגדרות -> יישובים. These are the only communities a coupon may be published to.
 *
 * A list stores its targets twice: `communityIds` is the source of truth (survives a rename on
 * the mekome side), while `towns` holds the names and is kept for lists that predate the id
 * migration. Names are resolved against the community catalogue rather than by zipping the two
 * arrays — the admin drops names it cannot resolve from `communityIds`, so the arrays are not
 * guaranteed to line up index-for-index.
 */

/** Same endpoint the admin panel reads its community catalogue from (the name is a misnomer). */
const COMMUNITY_CATALOGUE_URL = 'https://us-central1-mefarsemim-net.cloudfunctions.net/getCoupons';

export interface DistributionCommunity {
  id: number;
  name: string;
}

export interface DistributionList {
  id: string;
  name: string;
  communities: DistributionCommunity[];
}

interface CatalogueEntry {
  communityId: number | string;
  communityName: string;
}

const fetchCommunityCatalogue = async (): Promise<Map<number, string>> => {
  const resp = await fetch(COMMUNITY_CATALOGUE_URL, {
    method: 'GET',
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status} ${resp.statusText}`);

  const data = await resp.json();
  const body = data?.body;
  const list: CatalogueEntry[] = Array.isArray(body)
    ? body
    : typeof body === 'string'
      ? JSON.parse(body)
      : [];

  const byId = new Map<number, string>();
  list.forEach(entry => {
    const id = Number(entry?.communityId);
    if (!Number.isFinite(id)) return;
    byId.set(id, String(entry?.communityName ?? ''));
  });
  return byId;
};

/**
 * Loads the admin's distribution lists and resolves each one to concrete communities.
 * `lists` keeps the grouping (so the UI can show which רשימת ישובים a community came from);
 * `communities` is the flat deduplicated union, for lookups and totals.
 */
export const useDistributionLists = () => {
  const [lists, setLists] = useState<DistributionList[]>([]);
  const [communities, setCommunities] = useState<DistributionCommunity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The catalogue is a plain HTTP read, so it is fetched once and then joined against the
  // live Firestore snapshot below.
  const [catalogue, setCatalogue] = useState<Map<number, string> | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchCommunityCatalogue()
      .then(map => { if (!cancelled) setCatalogue(map); })
      // An unreachable catalogue is not fatal: lists still resolve through their `towns` names.
      .catch(() => { if (!cancelled) setCatalogue(new Map()); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!catalogue) return;

    const byName = new Map<string, number>();
    catalogue.forEach((name, id) => {
      const key = String(name).trim().normalize('NFKC');
      if (key && !byName.has(key)) byName.set(key, id);
    });

    const unsub = onSnapshot(
      collection(db, 'distributionLists'),
      snap => {
        const nextLists: DistributionList[] = [];

        snap.forEach(docSnap => {
          const data = docSnap.data() as any;
          const ids: number[] = Array.isArray(data?.communityIds)
            ? data.communityIds.map(Number).filter((n: number) => Number.isFinite(n))
            : [];
          const towns: string[] = Array.isArray(data?.towns)
            ? data.towns.map((t: any) => String(t ?? '').trim()).filter(Boolean)
            : [];

          const seen = new Set<number>();
          const resolved: DistributionCommunity[] = [];

          const push = (id: number, fallbackName: string) => {
            if (!Number.isFinite(id) || seen.has(id)) return;
            seen.add(id);
            resolved.push({ id, name: catalogue.get(id) || fallbackName || `קהילה ${id}` });
          };

          ids.forEach(id => push(id, ''));
          // Lists that were never migrated to ids carry names only.
          if (ids.length === 0) {
            towns.forEach(town => {
              const id = byName.get(town.normalize('NFKC'));
              if (id !== undefined) push(id, town);
            });
          }

          if (resolved.length === 0) return;

          resolved.sort((a, b) => a.name.localeCompare(b.name, 'he'));
          nextLists.push({
            id: docSnap.id,
            name: String(data?.name ?? '').trim() || 'רשימה ללא שם',
            communities: resolved,
          });
        });

        nextLists.sort((a, b) => a.name.localeCompare(b.name, 'he'));

        const flatSeen = new Set<number>();
        const flat: DistributionCommunity[] = [];
        nextLists.forEach(list =>
          list.communities.forEach(c => {
            if (flatSeen.has(c.id)) return;
            flatSeen.add(c.id);
            flat.push(c);
          }),
        );
        flat.sort((a, b) => a.name.localeCompare(b.name, 'he'));

        setLists(nextLists);
        setCommunities(flat);
        setError(null);
        setIsLoading(false);
      },
      err => {
        setError(err.message || 'טעינת רשימות הישובים נכשלה');
        setIsLoading(false);
      },
    );

    return () => unsub();
  }, [catalogue]);

  return { lists, communities, isLoading, error };
};
